import { MouseEventHandler, useContext, useEffect, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import Grid from '@mui/material/Grid'
import Tooltip from '@mui/material/Tooltip'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import Tab from '@mui/material/Tab'
import Collapse from '@mui/material/Collapse'

import { useParams } from 'react-router-dom'
import TimezoneSelector from '../../TimezoneSelector'

import ExpandIcon from '../../icons/ExpandIcon'

import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import BuildPageIcon from '../../UI/icons/BuildPageIcon'
import BusinessIcon from '@mui/icons-material/Business'
import CollapseIcon from '@mui/icons-material/MenuOpen'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import DashboardIcon from '@mui/icons-material/Dashboard'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import HelpCenterIcon from '@mui/icons-material/HelpCenter'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import SchoolIcon from '@mui/icons-material/School'
import SettingsIcon from '@mui/icons-material/Settings'
import TableChartIcon from '@mui/icons-material/TableChart'
import TransformIcon from '@mui/icons-material/Transform'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VegaV from 'vega-icon.svg'
import VegaWord from 'vega-word-only-color.svg';


import { getStorage, setStorage } from 'lib/utils'
import Box from '@mui/material/Box'
import { useFeatures } from 'providers/FeaturesProvider'
import ScopeProvider, { useScope } from './ScopeProvider'
import { getRemoteId } from 'lib/DataModel'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { UIMatch, UNSAFE_DataRouterContext as DataRouterContext, useMatches } from 'react-router'
import { DataRouterContextObject, RouteObject } from 'react-router/dist/lib/context'
import { drawerTransition, DRAWER_WIDTH, DRAWER_WIDTH_CLOSED, SIDEBAR_PADDING, SIDEBAR_ICON_SIZE, SIDEBAR_BUTTON_HEIGHT, INDICATOR_HEIGHT } from './styles'
import NavigateToSelected from './NavigateToSelected'
import createTabSection from './TabSection'
import TabContainer from './TabContainer'
import type { SxProps } from '@mui/system'
import SignOutButton from './SignOutButton'
import PatientListIcon from 'components/UI/icons/PatientList'

const DEFAULT_HELPSCOUT_URL = 'https://cygnus-education-inc.helpscoutdocs.com/' as const

export interface BaseTab {
  label: string
  icon: React.FC
  condition?: (a: unknown) => boolean
}

export interface RouteTab extends BaseTab {
  path: string
}

export interface LinkTab extends BaseTab {
  url: string | ((a: unknown) => string)
}

export interface ClickTab extends BaseTab {
  onClick: MouseEventHandler
}

export type Tab = RouteTab | LinkTab | ClickTab

export function isRouteTab(tab: Tab): tab is RouteTab {
  return 'path' in tab
}

export function isLinkTab(tab: Tab): tab is LinkTab {
  return 'url' in tab
}

export function isClickTab(tab: Tab): tab is ClickTab {
  return 'onClick' in tab
}

export const Tabs = {
  Global: {
    Dashboard:     { label: 'Dashboard',     icon: DashboardIcon, path: '/dashboard' },
    Clients:       { label: 'Clients',       icon: SchoolIcon, path: '/system/clients' },
    Vendors:       { label: 'Vendors',       icon: BusinessIcon, path: '/system/vendors' },
    Performance:   { label: 'Performance',   icon: TableChartIcon, path: '/performance' },
    Leads:         { label: 'Leads',         icon: FindInPageIcon, path: '/leads' },
    Users:         { label: 'Users',         icon: PersonIcon, path: '/organizations/:organizationId/users' },
    Groups:        { label: 'Groups',        icon: PatientListIcon, path: '/groups' },
    Organizations: { label: 'Organizations', icon: PeopleIcon, path: '/system/organizations' },
    Chat:          { label: 'Chat',          icon: ContactSupportIcon, onClick: (_event: unknown) => window.Beacon('open'),  condition: (_: unknown) => !!window.Beacon  },
    HelpCenter:    { label: 'Help Center',   icon: HelpCenterIcon, url: ({ getFeatureConfig }) => getFeatureConfig('help_center', 'url', DEFAULT_HELPSCOUT_URL),
                                                                                                                             condition: ({ isFeatureFlagEnabled }) => isFeatureFlagEnabled('help_center', true) },
  },
  Client: {
    Dashboard: { label: 'Dashboard', icon: DashboardIcon, path: '/clients/:clientId/dashboard' },
    Manage:    { label: 'Manage',    icon: SettingsIcon, path: '/clients/:clientId/allocations' },
    Pacing:    { label: 'Pacing',    icon: TrendingUpIcon, path: '/clients/:clientId/pacing' },
  },
  Vendor: {
    Dashboard:    { label: 'Dashboard',     icon: DashboardIcon, path: '/contracts/:contractId/dashboard' },
    Allocations:  { label: 'Allocations',   icon: AssignmentTurnedInIcon, path: '/contracts/:contractId/allocations' },
    LandingPages: { label: 'Landing Pages', icon: BuildPageIcon, path: '/vendors/:vendorId/landing-pages', condition: ({ currentVendor }) => !!currentVendor?.attributes?.permitLandingPages },
    Integrations: { label: 'Integrations',  icon: TransformIcon, path: '/contracts/:contractId/integrations', condition: ({ currentContract }) => !!currentContract?.attributes?.canPostLeads },
    Documents:    { label: 'Documents',     icon: DescriptionOutlinedIcon, path: '/contracts/:contractId/documents' },
  },
}

function findRoute(tab: Tab, routes: RouteObject[]) {
  let matchingRoute: RouteObject | undefined

  for(const route of routes) {
    if(route.handle?.tab === tab) {
      matchingRoute = route
      break
    }
    if(route.children) {
      matchingRoute = findRoute(tab, route.children)
      if(matchingRoute) {
        break
      }
    }
  }

  return matchingRoute
}

function getActiveTab(matches: UIMatch[]) {
  let tab: RouteTab | undefined
  for(const match of matches) {
    if(match.handle?.['tab']) {
      tab = match.handle['tab']
    }
  }

  return tab
}

function getRoutableTabs(tabs: Record<string,Tab>, routes: RouteObject[]) {
  return Object.values(tabs).filter(tab => !isRouteTab(tab) || findRoute(tab, routes))
}

const RouterTabs = function({showLabels}) {
  const { router: { routes } } = useContext(DataRouterContext) as DataRouterContextObject
  const routeMatches = useMatches()

  const { currentOrganization, accessibleVendors, accessibleClients } = useCurrentUser()
  const { getFeatureConfig, isFeatureFlagEnabled } = useFeatures()

  const { clientId, vendorId, contractId } = useParams()
  const { currentClient, currentVendor, setCurrentClient, setCurrentVendor, currentContract, setCurrentContract, contractsForVendor } = useScope()
  const prioritizedClientId = clientId ?? (currentClient && getRemoteId(currentClient)) ?? 'missingClientId'
  const prioritizedVendorId = vendorId ?? (currentVendor && getRemoteId(currentVendor)) ?? 'missingVendorId'
  const prioritizedContractId = contractId ?? (currentContract && getRemoteId(currentContract)) ?? 'missingContractId'

  const expandMacros = (path: string | undefined) => path
    ?.replace(':organizationId', currentOrganization.id)
    .replace(':clientId', prioritizedClientId)
    .replace(':vendorId', prioritizedVendorId)
    .replace(':contractId', prioritizedContractId)

  const conditionProps = { currentVendor, currentContract, isFeatureFlagEnabled }

  const selectedTab = getActiveTab(routeMatches)

  const routableGlobalTabs = getRoutableTabs(Tabs.Global, routes)
  const hasGlobalDashboard = routableGlobalTabs.some(tab => tab.label === 'Dashboard')

  const globalTabs: Tab[] = []
  const clientTabs: Tab[] = getRoutableTabs(Tabs.Client, routes)
  const vendorTabs: Tab[] = []
  const contractTabs: Tab[] = getRoutableTabs(Tabs.Vendor, routes)

  // If the global dashboard is present, global tabs are in the globalTabs section
  if(hasGlobalDashboard) {
    globalTabs.push(...routableGlobalTabs)
  }
  // Otherwise, if there are client tabs to be shown, global tabs are appended to the clientTabs section
  else if(clientTabs.length > 0) {
    clientTabs.push(...routableGlobalTabs)
  }
  // Otherwise, global tabs are included in the contract tabs if present
  else if(currentContract) {
    contractTabs.push(...routableGlobalTabs)
  }
  // Otherwise, they are in the vendor tabs
  else {
    vendorTabs.push(...routableGlobalTabs)
  }

  return (
    <TabContainer
      orientation="vertical"
      value={selectedTab?.path ?? false}
      sx={{
        marginLeft: `-${SIDEBAR_PADDING}px`,
        marginRight: `-${SIDEBAR_PADDING}px`,
        marginTop: '8px',
      }}
      indicatorColor="primary"
      TabIndicatorProps={{
        sx: {
          width: 3,
          height: '25px !important',
          borderRadius: '5px 0px 0px 5px',
          marginTop: `${(SIDEBAR_BUTTON_HEIGHT - INDICATOR_HEIGHT) / 2}px`,
        }
      }}
      tabSections={[
        createTabSection({
          title: "Global",
          tabs: globalTabs,
          expandMacros: expandMacros,
          conditionProps: conditionProps,
          getFeatureConfig: getFeatureConfig,
          showLabels: showLabels,
          showHeading: globalTabs.length > 0,
        }),
        currentClient && createTabSection({
          title: "Client",
          entityType: 'client',
          entities: accessibleClients,
          currentEntity: currentClient,
          setCurrentEntity: setCurrentClient,
          tabs: clientTabs,
          expandMacros: expandMacros,
          conditionProps: conditionProps,
          getFeatureConfig: getFeatureConfig,
          showLabels: showLabels,
          showHeading: clientTabs.length > 0,
        }),
        currentVendor && createTabSection({
          title: "Vendor",
          entityType: 'vendor',
          entities: accessibleVendors,
          currentEntity: currentVendor,
          setCurrentEntity: setCurrentVendor,
          tabs: vendorTabs,
          expandMacros: expandMacros,
          conditionProps: conditionProps,
          getFeatureConfig: getFeatureConfig,
          showLabels: showLabels,
          showHeading: contractTabs.length > 0,
        }),
        currentVendor && currentContract && createTabSection({
          title: `${currentVendor.attributes.name} Client`,
          entities: contractsForVendor,
          entityType: 'contract',
          currentEntity: currentContract,
          setCurrentEntity: setCurrentContract,
          tabs: contractTabs,
          expandMacros: expandMacros,
          conditionProps: conditionProps,
          getFeatureConfig: getFeatureConfig,
          showLabels: showLabels,
          showHeading: contractTabs.length > 0 && contractsForVendor.length > 0,
        })
      ]}
    />
  )
}

export type SidebarProps = {
  authenticityToken: string
  initiallyExpanded?: boolean | null
  sx?: SxProps
}

export default function Sidebar({authenticityToken, initiallyExpanded=null, sx}: SidebarProps) {
  // Local storage added for desktop only since mobile also uses desktop view
  const storageDesktopOpen = getStorage('MainSidebar.desktopOpen')
  const [open, setOpen] = useState((initiallyExpanded !== null ? initiallyExpanded : storageDesktopOpen !== undefined ? storageDesktopOpen : true))

  const handleDrawerToggleDesktop = () => {
    setOpen(!open)
  }

  useEffect(() => {
    setStorage('MainSidebar.desktopOpen', open)
  }, [open])

  const drawer = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          component="img"
          alt='V'
          src={VegaV}
          sx={{
            mt: '-5px',
            ml: '-2px',
            height: 31,
            width: 33,
          }}
        />
        <Collapse orientation="horizontal" in={open}>
          <Box
            component="img"
            alt='VEGA'
            src={VegaWord}
            sx={{
              mt: '1px',
              ml: '15px',
              height: 32,
              width: 81
            }}
          />
        </Collapse>
      </Box>
      <Grid container direction="column" justifyContent="space-between" sx={{ flexGrow: 1 }}>
        <Grid item sx={{mb: 5}}>
          <Tooltip title={open ? 'Collapse menu' : 'Expand menu'}>
            <IconButton
              sx={{
                color: 'text.secondary',
                width: SIDEBAR_ICON_SIZE,
                height: SIDEBAR_ICON_SIZE,
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={handleDrawerToggleDesktop}
            >
              {open && (
                <CollapseIcon/>
              ) || (
                <ExpandIcon/>
              )}
            </IconButton>
          </Tooltip>
          <RouterTabs showLabels={open}/>
        </Grid>
        <Grid item>
          <Grid container direction="column" spacing={4}>
            <Grid item>
              <TimezoneSelector isExpanded={open} />
            </Grid>
            <Grid item>
              <form noValidate action="/users/sign_out" method="POST">
                <input type="hidden" name="authenticity_token" value={authenticityToken} />
                <input type="hidden" name="_method" value="DELETE"/>
                <SignOutButton
                  open={open}
                  onClick={event => (event.currentTarget as HTMLElement).closest('form')?.submit()}
                  endIcon={<PowerSettingsNewIcon/>}
                >
                  <Collapse orientation="horizontal" in={open}>
                    <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      Sign out
                    </Box>
                  </Collapse>
                </SignOutButton>
              </form>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )

  return (
    <ScopeProvider>
      <NavigateToSelected/>
      <Drawer
        role="navigation"
        aria-roledescription="sidebar"
        variant="persistent"
        anchor="left"
        open
        sx={[
          {
            height: '100%',
            transition: theme => drawerTransition(theme, 'width'),
            width: open ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED,
            '& .MuiDrawer-paper': {
              backgroundColor: 'background.paper',
              color: 'text.secondary',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: `38px ${SIDEBAR_PADDING}px ${SIDEBAR_PADDING}px ${SIDEBAR_PADDING}px`,
              transition: theme => drawerTransition(theme, 'width'),
              width: open ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED,
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {drawer}
      </Drawer>
    </ScopeProvider>
  )
}
